import { useEffect, useMemo, useRef, useState } from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { apiUrl } from "../../utils/api";
import { VEHICLE_CLASSES, type VehicleClassDef } from "../risikoKfzStep/vehicleClasses";
import { VEHICLE_CLASS_TO_KEY } from "./data";
import type { KlasseKey, RiskSummary } from "./types";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
  },
  filterBar: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "14px",
    paddingRight: "14px",
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
  },
  filterRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    rowGap: "8px",
    flexWrap: "wrap",
  },
  filterLabel: {
    fontSize: "0.75rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: BRAND.muted,
    flexShrink: 0,
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: "6px",
    rowGap: "6px",
  },
  chip: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.borderRadius("999px"),
    ...shorthands.border("1px", "solid", BRAND.border),
    transitionProperty: "border-color, background-color, color",
    transitionDuration: "120ms",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
    },
  },
  chipActive: {
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    ...shorthands.borderColor(BRAND.purple),
  },
  chipIcon: {
    width: "16px",
    height: "12px",
    color: "currentColor",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  search: {
    width: "100%",
    boxSizing: "border-box",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    outlineWidth: 0,
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
      boxShadow: `0 0 0 3px ${BRAND.purple}33`,
    },
  },
  summaryRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    flexWrap: "wrap",
    fontSize: "0.85rem",
    color: BRAND.muted,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  card: {
    backgroundColor: BRAND.white,
    ...shorthands.border("2px", "solid", BRAND.border),
    ...shorthands.borderRadius("10px"),
    overflow: "hidden",
    transitionProperty: "border-color, box-shadow",
    transitionDuration: "120ms",
  },
  cardActive: {
    ...shorthands.borderColor(BRAND.pillarOrange),
    boxShadow: "0 0 0 3px rgba(230,106,44,0.16)",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "14px",
    paddingRight: "14px",
    cursor: "pointer",
    appearance: "none",
    backgroundColor: "transparent",
    backgroundImage: "none",
    width: "100%",
    fontFamily: BRAND.fontPrimary,
    textAlign: "left",
    color: BRAND.ink,
    ...shorthands.border("0"),
  },
  classIcon: {
    width: "36px",
    height: "36px",
    flexShrink: 0,
    ...shorthands.borderRadius("8px"),
    backgroundColor: "rgba(230,106,44,0.10)",
    color: BRAND.pillarOrange,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  cardMain: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
  },
  cardTitle: {
    fontSize: "0.95rem",
    fontWeight: 700,
    color: BRAND.ink,
  },
  cardSub: {
    fontSize: "0.78rem",
    color: BRAND.muted,
  },
  badge: {
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    paddingTop: "3px",
    paddingBottom: "3px",
    paddingLeft: "8px",
    paddingRight: "8px",
    ...shorthands.borderRadius("999px"),
    backgroundColor: BRAND.bg,
    color: BRAND.ink,
    flexShrink: 0,
  },
  badgeOn: {
    backgroundColor: BRAND.pillarOrange,
    color: BRAND.white,
  },
  chevron: {
    color: BRAND.muted,
    fontSize: "1.2rem",
    fontWeight: 700,
    width: "16px",
    textAlign: "center",
    transitionProperty: "transform",
    transitionDuration: "150ms",
  },
  chevronOpen: {
    transform: "rotate(90deg)",
  },
  body: {
    paddingTop: "12px",
    paddingBottom: "14px",
    paddingLeft: "14px",
    paddingRight: "14px",
    backgroundColor: "#fbf7fc",
    display: "grid",
    gridTemplateColumns: "1fr",
    rowGap: "8px",
    columnGap: "16px",
    "@media (min-width: 720px)": {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  bodyRow: {
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
  },
  bodyLabel: {
    fontSize: "0.7rem",
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: BRAND.muted,
  },
  bodyValue: {
    fontSize: "0.92rem",
    color: BRAND.ink,
  },
  missing: {
    color: "#a99fa8",
    fontStyle: "italic",
  },
  empty: {
    paddingTop: "30px",
    paddingBottom: "30px",
    textAlign: "center",
    color: BRAND.muted,
    fontSize: "0.95rem",
    backgroundColor: BRAND.bg,
    ...shorthands.borderRadius("10px"),
    ...shorthands.border("1px", "dashed", BRAND.borderDashed),
  },
  loading: {
    paddingTop: "30px",
    paddingBottom: "30px",
    textAlign: "center",
    color: BRAND.muted,
    fontSize: "0.95rem",
  },
  notice: {
    fontSize: "0.85rem",
    color: BRAND.muted,
    fontStyle: "italic",
    backgroundColor: BRAND.bg,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "dashed", BRAND.borderDashed),
  },
  hint: {
    fontSize: "0.82rem",
    color: BRAND.gold,
    backgroundColor: "#fff5e6",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "solid", "#f3d4a3"),
  },
});

const SmallClassIcon = ({ iconKey }: { iconKey: VehicleClassDef["iconKey"] }) => {
  const common = {
    width: 20,
    height: 14,
    viewBox: "0 0 56 36",
    fill: "none" as const,
    stroke: "currentColor" as const,
    strokeWidth: 1.6,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  switch (iconKey) {
    case "pkw":
      return (
        <svg {...common}>
          <path d="M6 24l3-9a3 3 0 0 1 2.8-2h22.4a3 3 0 0 1 2.8 2l3 9" />
          <path d="M4 24h44v6H4z" />
          <circle cx="14" cy="28" r="2.5" />
          <circle cx="38" cy="28" r="2.5" />
        </svg>
      );
    case "lieferwagen":
      return (
        <svg {...common}>
          <path d="M4 12h26v15H4z" />
          <path d="M30 16h11l5 7v4H30z" />
        </svg>
      );
    case "lkw":
      return (
        <svg {...common}>
          <path d="M4 8h22v18H4z" />
          <path d="M26 14h12l8 8v4H26z" />
        </svg>
      );
    case "zugmaschine":
      return (
        <svg {...common}>
          <path d="M4 16h12l4-8h6l3 8h3v9H4z" />
          <path d="M30 18h22v9H30z" />
        </svg>
      );
    case "bus":
      return (
        <svg {...common}>
          <path d="M5 8h44v18H5z" />
        </svg>
      );
    case "anhaenger":
      return (
        <svg {...common}>
          <path d="M4 14h44v12H4z" />
        </svg>
      );
    case "arbeitsmaschine":
      return (
        <svg {...common}>
          <path d="M4 24h32v6H4z" />
          <path d="M14 24V12h12v12" />
        </svg>
      );
    case "stapler":
      return (
        <svg {...common}>
          <path d="M14 22V10h12v12" />
          <path d="M26 26h20" />
        </svg>
      );
    case "krad":
      return (
        <svg {...common}>
          <circle cx="12" cy="24" r="6" />
          <circle cx="44" cy="24" r="6" />
        </svg>
      );
    case "sonderfahrzeug":
      return (
        <svg {...common}>
          <path d="M6 24h44" />
          <path d="M10 24v-8h14l4-4h12v12" />
        </svg>
      );
  }
};

type AvailableRisk = {
  id: number;
  name: string;
  characteristics: Record<string, string>;
  contactId: number | null;
  contactName: string | null;
};

const resolveKlasse = (chars: Record<string, string>): KlasseKey | null => {
  const v = chars.vehicleClass ?? "";
  if (!v) return null;
  for (const m of VEHICLE_CLASS_TO_KEY) {
    if (v.includes(m.contains)) return m.key;
  }
  return null;
};

const buildDisplayName = (c: any, fallbackId: number): string => {
  if (!c) return `Kontakt ${fallbackId}`;
  if (typeof c.displayName === "string" && c.displayName.trim()) return c.displayName;
  const first = c.firstName ?? "";
  const last = c.lastname ?? "";
  if (last && first) return `${last}, ${first}`;
  if (last) return String(last);
  if (first) return String(first);
  if (c.companyName) return String(c.companyName);
  return `Kontakt ${fallbackId}`;
};

const normalizeRiskList = (raw: any[]): AvailableRisk[] => {
  return raw
    .filter((r) => r && r.id)
    .map((r) => {
      const charsArr: Array<{ nameBezeichnung?: string; riskCharacteristicValue?: string }> =
        Array.isArray(r.characteristics) ? r.characteristics : [];
      const chars: Record<string, string> = {};
      for (const c of charsArr) {
        if (c.nameBezeichnung) chars[c.nameBezeichnung] = String(c.riskCharacteristicValue ?? "");
      }
      const directContact = r.contact ?? null;
      const contactId = directContact?.id ?? null;
      const contactName = contactId ? buildDisplayName(directContact, contactId) : null;
      return {
        id: r.id as number,
        name: (r.name ?? "") as string,
        characteristics: chars,
        contactId,
        contactName,
      };
    });
};

type Props = {
  preselectedRiskIds?: number[];
  contactIdHint?: number | null;
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  isEinzelvertrag: boolean;
  /**
   * Sammelvertrag-Modus: nach dem Laden werden automatisch alle Risiken
   * des Kontakts ausgewählt. Manuelles Abwählen wird sofort wieder ergänzt.
   */
  autoSelectAll?: boolean;
  /** Already loaded risks (for preselection / detail display) */
  preloaded?: RiskSummary[];
};

const RisikoAuswahlSection = ({
  preselectedRiskIds,
  contactIdHint,
  selectedIds,
  onChange,
  isEinzelvertrag,
  autoSelectAll = false,
  preloaded,
}: Props) => {
  const styles = useStyles();
  const [risks, setRisks] = useState<AvailableRisk[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [classFilter, setClassFilter] = useState<Set<KlasseKey>>(new Set());
  const [contactFilter, setContactFilter] = useState<number | "all">("all");
  const [search, setSearch] = useState("");
  const [openMap, setOpenMap] = useState<Record<number, boolean>>({});

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const url = contactIdHint
          ? apiUrl(`risk-objects/by-contact/${contactIdHint}`)
          : apiUrl("risk-objects");
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const list = normalizeRiskList(Array.isArray(data) ? data : []);
        // Filter to KFZ-relevant risks only (those with a resolvable Klasse).
        const kfzRisks = list.filter((r) => resolveKlasse(r.characteristics) !== null);
        // Merge in preloaded summaries (in case the preselected risk isn't in the by-contact list)
        if (preloaded && preloaded.length) {
          for (const p of preloaded) {
            if (kfzRisks.some((r) => r.id === p.id)) continue;
            kfzRisks.unshift({
              id: p.id,
              name: p.name,
              characteristics: p.characteristics,
              contactId: p.contacts[0]?.id ?? null,
              contactName: p.contacts[0]?.displayName ?? null,
            });
          }
        }
        setRisks(kfzRisks);
        setErrorMsg(null);
      } catch (e) {
        console.error(e);
        setErrorMsg("Risiken konnten nicht geladen werden.");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [contactIdHint, preloaded]);

  // Auto-preselect on first load (only once)
  const didPreselectRef = useRef(false);
  useEffect(() => {
    if (didPreselectRef.current) return;
    if (!preselectedRiskIds || preselectedRiskIds.length === 0) return;
    didPreselectRef.current = true;
    const missing = preselectedRiskIds.filter((id) => !selectedIds.includes(id));
    if (missing.length === 0) return;
    onChange([...missing, ...selectedIds]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedRiskIds]);

  // Sammelvertrag: select every loaded risk for the contact.
  useEffect(() => {
    if (!autoSelectAll) return;
    if (loading) return;
    if (risks.length === 0) return;
    const allIds = risks.map((r) => r.id);
    const allSelected =
      allIds.length === selectedIds.length &&
      allIds.every((id) => selectedIds.includes(id));
    if (allSelected) return;
    onChange(allIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSelectAll, loading, risks]);

  const contactOptions = useMemo(() => {
    const map = new Map<number, string>();
    for (const r of risks) {
      if (r.contactId && r.contactName && !map.has(r.contactId)) {
        map.set(r.contactId, r.contactName);
      }
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [risks]);

  const filteredRisks = useMemo(() => {
    const q = search.trim().toLowerCase();
    return risks.filter((r) => {
      const klasse = resolveKlasse(r.characteristics);
      if (classFilter.size > 0 && (!klasse || !classFilter.has(klasse))) return false;
      if (contactFilter !== "all" && r.contactId !== contactFilter) return false;
      if (q) {
        const hay = [
          r.name,
          r.characteristics.licensePlate,
          r.characteristics.vin,
          r.characteristics.manufacturer,
          r.characteristics.typeName,
          r.contactName ?? "",
        ]
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [risks, search, classFilter, contactFilter]);

  const toggleClassFilter = (key: KlasseKey) => {
    setClassFilter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelect = (id: number) => {
    if (autoSelectAll) return;
    if (isEinzelvertrag) {
      onChange(selectedIds.includes(id) ? [] : [id]);
      return;
    }
    onChange(
      selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id],
    );
  };

  const toggleOpen = (id: number) => setOpenMap((p) => ({ ...p, [id]: !p[id] }));

  const klasseKeyToVehicleDef = (k: KlasseKey | null): VehicleClassDef | null => {
    if (!k) return null;
    // KlasseKey -> iconKey mapping: matches contractStep/data.ts KlasseKey ordering
    const mapping: Record<KlasseKey, VehicleClassDef["iconKey"]> = {
      pkw: "pkw",
      lieferwagen: "lieferwagen",
      lkw: "lkw",
      zugmaschine: "zugmaschine",
      busse: "bus",
      anhaenger: "anhaenger",
      arbeitsmaschinen: "arbeitsmaschine",
      gabelstapler: "stapler",
      krad: "krad",
      sonderfahrzeuge: "sonderfahrzeug",
    };
    return VEHICLE_CLASSES.find((v) => v.iconKey === mapping[k]) ?? null;
  };

  return (
    <div className={styles.root}>
      {isEinzelvertrag && (
        <div className={styles.hint}>
          Für Einzelverträge wird genau ein Risiko ausgewählt. Eine neue Auswahl ersetzt die vorige.
        </div>
      )}
      {autoSelectAll && (
        <div className={styles.hint}>
          Für Sammelverträge werden automatisch alle Risiken des Kontakts dem Vertrag zugeordnet.
        </div>
      )}

      <div className={styles.filterBar}>
        <div className={styles.filterRow}>
          <span className={styles.filterLabel}>Suche</span>
          <input
            className={styles.search}
            type="search"
            value={search}
            placeholder="Kennzeichen, VIN, Hersteller, Halter…"
            onChange={(e) => setSearch(e.target.value)}
            style={{ flexGrow: 1, minWidth: 180 }}
          />
        </div>
        {contactOptions.length > 1 && (
          <div className={styles.filterRow}>
            <span className={styles.filterLabel}>Halter</span>
            <div className={styles.chips}>
              <button
                type="button"
                className={mergeClasses(styles.chip, (contactFilter === "all") && styles.chipActive)}
                onClick={() => setContactFilter("all")}
              >
                Alle ({risks.length})
              </button>
              {contactOptions.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={mergeClasses(styles.chip, (contactFilter === c.id) && styles.chipActive)}
                  onClick={() => setContactFilter(c.id)}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className={styles.filterRow}>
          <span className={styles.filterLabel}>Klasse</span>
          <div className={styles.chips}>
            {VEHICLE_CLASSES.map((vc) => {
              // Reverse iconKey → KlasseKey
              const reverse: Record<VehicleClassDef["iconKey"], KlasseKey> = {
                pkw: "pkw",
                lieferwagen: "lieferwagen",
                lkw: "lkw",
                zugmaschine: "zugmaschine",
                bus: "busse",
                anhaenger: "anhaenger",
                arbeitsmaschine: "arbeitsmaschinen",
                stapler: "gabelstapler",
                krad: "krad",
                sonderfahrzeug: "sonderfahrzeuge",
              };
              const k = reverse[vc.iconKey];
              const active = classFilter.has(k);
              return (
                <button
                  key={vc.iconKey}
                  type="button"
                  className={mergeClasses(styles.chip, active && styles.chipActive)}
                  onClick={() => toggleClassFilter(k)}
                >
                  <span className={styles.chipIcon}>
                    <SmallClassIcon iconKey={vc.iconKey} />
                  </span>
                  {vc.short}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className={styles.summaryRow}>
        <span>
          {filteredRisks.length} Risiko{filteredRisks.length === 1 ? "" : "en"} angezeigt
        </span>
        <span>·</span>
        <span style={{ fontWeight: 700, color: BRAND.ink }}>
          {selectedIds.length} ausgewählt
        </span>
      </div>

      {loading ? (
        <div className={styles.loading}>Lade Risiken…</div>
      ) : errorMsg ? (
        <div className={styles.notice}>{errorMsg}</div>
      ) : filteredRisks.length === 0 ? (
        <div className={styles.empty}>
          Keine Risiken gefunden. Passen Sie die Filter an oder legen Sie zuerst ein Risikoobjekt
          an.
        </div>
      ) : (
        <div className={styles.list}>
          {filteredRisks.map((r) => {
            const klasse = resolveKlasse(r.characteristics);
            const vehDef = klasseKeyToVehicleDef(klasse);
            const isSelected = selectedIds.includes(r.id);
            const isOpen = !!openMap[r.id];
            const c = r.characteristics;
            const plate = c.licensePlate || "—";
            const vin = c.vin || null;
            const make = `${c.manufacturer ?? ""} ${c.typeName ?? ""}`.trim();
            return (
              <div
                key={r.id}
                className={mergeClasses(styles.card, isSelected && styles.cardActive)}
              >
                <button
                  type="button"
                  className={styles.cardHeader}
                  onClick={() => toggleSelect(r.id)}
                >
                  <span className={styles.classIcon}>
                    {vehDef ? <SmallClassIcon iconKey={vehDef.iconKey} /> : "?"}
                  </span>
                  <span className={styles.cardMain}>
                    <span className={styles.cardTitle}>
                      {plate}
                      {make ? ` · ${make}` : ""}
                    </span>
                    <span className={styles.cardSub}>
                      {vehDef ? vehDef.short : "Unbekannte Klasse"}
                      {r.contactName ? ` · Halter: ${r.contactName}` : ""}
                      {vin ? ` · VIN ${vin}` : ""}
                    </span>
                  </span>
                  <span className={mergeClasses(styles.badge, isSelected && styles.badgeOn)}>
                    {isSelected ? "Im Vertrag" : "Hinzufügen"}
                  </span>
                  <span
                    role="button"
                    aria-expanded={isOpen}
                    aria-label="Details ein-/ausklappen"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleOpen(r.id);
                    }}
                    className={mergeClasses(styles.chevron, isOpen && styles.chevronOpen)}
                  >
                    ›
                  </span>
                </button>
                {isOpen && (
                  <div className={styles.body}>
                    {[
                      { label: "Kennzeichen", value: c.licensePlate },
                      { label: "Fahrgestellnummer (VIN)", value: c.vin },
                      { label: "Risiko-ID", value: String(r.id) },
                      { label: "Hersteller / Typ", value: make || null },
                      { label: "Antrieb", value: c.driveType },
                      { label: "Leistung (kW)", value: c.powerKw },
                      { label: "Erstzulassung", value: c.marketValueDate },
                      { label: "Verwendung", value: c.vehicleUsage },
                      { label: "Wert", value: c.vehicleValue },
                      { label: "Halter", value: r.contactName },
                    ].map((row) => (
                      <div key={row.label} className={styles.bodyRow}>
                        <span className={styles.bodyLabel}>{row.label}</span>
                        <span
                          className={mergeClasses(styles.bodyValue, !row.value && styles.missing)}
                        >
                          {row.value ? String(row.value) : "Nicht erfasst"}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RisikoAuswahlSection;
